import React, { useMemo } from 'react';
import { Box, Card, CardContent, Stack, Tooltip, Typography } from '@mui/material';
import { useCommonTranslation } from 'c-translation';
import PostingStatusDot, { postingStatusColor } from './PostingStatusDot';
import type { StatusedPostingLine } from './lib';
import { PostingStatus, postingBreakdown } from './lib';

const SEGMENTS = [PostingStatus.Within, PostingStatus.Out, PostingStatus.Awaiting];

type Props = {
    title: string;
    lines: StatusedPostingLine[];
    groupBy: 'owner' | 'state' | 'market';
};

const PostingBreakdownCard: React.FC<Props> = ({ title, lines, groupBy }) => {
    const t = useCommonTranslation();
    const rows = useMemo(() => postingBreakdown(lines, groupBy), [lines, groupBy]);
    const p = 'Modules.Main.Campaigns.ClassicPostings';

    return (
        <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
                <Typography variant="h6" mb={1.5}>
                    {title}
                </Typography>
                {/* One grid for every row, so the bars line up whatever the text either side */}
                <Box
                    data-testid="posting-breakdown-grid"
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: '1fr',
                            md: 'minmax(140px, 1.2fr) minmax(120px, 2fr) auto',
                        },
                        alignItems: 'center',
                        columnGap: 2,
                        rowGap: { xs: 0.5, md: 1.25 },
                    }}
                >
                    {rows.map(row => (
                        <React.Fragment key={row.key}>
                            <Box minWidth={0}>
                                <Typography variant="body2" fontWeight={600} noWrap title={row.key}>
                                    {row.key || '—'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {t(
                                        row.total === 1
                                            ? `${p}.Breakdown.unit`
                                            : `${p}.Breakdown.units`,
                                        { count: row.total },
                                    )}
                                </Typography>
                            </Box>
                            <Tooltip
                                title={SEGMENTS.filter(s => row.counts[s])
                                    .map(s => `${row.counts[s]} ${t(`${p}.Status.${s}`)}`)
                                    .join(' · ')}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        height: 10,
                                        borderRadius: 5,
                                        overflow: 'hidden',
                                        bgcolor: 'action.hover',
                                    }}
                                >
                                    {SEGMENTS.map(s =>
                                        row.counts[s] ? (
                                            <Box
                                                key={s}
                                                sx={theme => ({
                                                    width: `${(row.counts[s] / row.total) * 100}%`,
                                                    bgcolor: postingStatusColor(theme, s),
                                                })}
                                            />
                                        ) : null,
                                    )}
                                </Box>
                            </Tooltip>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    fontVariantNumeric: 'tabular-nums',
                                    whiteSpace: 'nowrap',
                                    mb: { xs: 1, md: 0 },
                                }}
                            >
                                {[
                                    t(`${p}.Breakdown.inWindow`, {
                                        within: row.counts.in,
                                        total: row.total,
                                    }),
                                    row.avg != null &&
                                        t(`${p}.Breakdown.avg`, { days: row.avg.toFixed(1) }),
                                    row.worst != null &&
                                        t(`${p}.Breakdown.worst`, { days: row.worst }),
                                    row.counts.wait > 0 &&
                                        t(`${p}.Breakdown.awaiting`, { count: row.counts.wait }),
                                ]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </Typography>
                        </React.Fragment>
                    ))}
                </Box>
                <Stack direction="row" gap={2} mt={2} flexWrap="wrap">
                    {SEGMENTS.map(s => (
                        <PostingStatusDot key={s} status={s} label={t(`${p}.Status.${s}`)} />
                    ))}
                </Stack>
            </CardContent>
        </Card>
    );
};

export default PostingBreakdownCard;
